import { createUseStyles, useTheme } from "react-jss"
import FilterGroup from "../../features/Monitor/FilterGroup"
import { useAppDispatch, useAppSelector } from "../../util/hooks"
import {
  deepDiveAsync,
  flowDiagramAsync,
  formatDate,
  gantActiveSelector,
  gantChartAsync,
  lastRunAsync,
  lastRunAsynclastRunDateToCheckRefreshStatusAsync,
  lastRunDateSelector,
  openDialog,
  pipelineCardsAsync,
  refreshPipeLineAsync,
  refreshStatusPipeLineAsync,
  selectedRunDetailsSelector,
  setAlertData,
  setAlertData2,
  timeChange,
  timeZoneChange,
  toggleNewPipeLineScreen,
  updateRefreshStatus,
} from "../../features/Monitor/MonitorSlice"
import Refresh from "../../icons/refresh.svg"
import RefreshCircle from "../../icons/refresh-circle.svg"
import {
  ALERT_STATUS_MSGS,
  API_STATUS,
  SCREENS_DETAILS,
} from "../../util/StaticData"
import { useEffect, useState } from "react"
import { useLocation } from "react-router-dom"
import CustomIcon from "../CustomIcon"
import { NEW_PIPELINE_SCREENS } from "../../util/NewPipelineConfig"
import { hitRefresh } from "../../features/Quality/DataQualitySlice"

const useStyles = createUseStyles({
  container: {
    height: "fit-content",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingBottom: 0,
  },
  date: {
    color: "#000",
    fontFeatureSettings: "'clig' off, 'liga' off",
    fontFamily: ({ theme }: any) => theme.primaryFontFamily,
    fontSize: "11px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "14px",
  },
  dateLabel: {
    color: "#000",
    fontFeatureSettings: "'clig' off, 'liga' off",
    fontFamily: ({ theme }: any) => theme.primaryFontFamily,
    fontSize: "11px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "14px",
  },
  newPipeLineBtn: {
    borderRadius: "7px",
    background: "var(--primary-dp-color)",
    height: "27px",
    width: "121px",
    color: "#000000",
    fontFamily: "Roboto",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "normal",
  },
  refresh: {
    color: "#000000",
    textAlign: "center",
    fontFamily: "Roboto",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "16px",
    letterSpacing: "0.12px",
    cursor: ({ refreshStatus }) => (refreshStatus ? "default" : "pointer"),
  },
  separator: {
    height: "16px",
    backgroundColor: "black",
    margin: "0 8px",
    width: "2px",
  },
  flexContainer: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  flexContainer2: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  columnContainer: {
    display: "flex",
    flexDirection: "column",
  },
})

export default function Controls() {
  const dispatch = useAppDispatch()
  const lastRunDate = useAppSelector(lastRunDateSelector)
  const theme = useTheme()
  const refreshStatus = useAppSelector(
    (store) => store.monitor.newPipeLine.refreshInprogress,
  )
  const qualityRefreshStatus = useAppSelector(
    (store) => store.dataQuality.refreshStatus,
  )
  const activeGantSelection = useAppSelector(gantActiveSelector)
  const selectedRunDetails = useAppSelector(selectedRunDetailsSelector)

  const classes = useStyles({ theme, refreshStatus })

  const handleNewPipeLineRun = () => {
    dispatch(openDialog())
    dispatch(timeZoneChange(""))
    dispatch(timeChange(null))
    dispatch(toggleNewPipeLineScreen(NEW_PIPELINE_SCREENS.DETAILS))
  }

  const refresh_status = async () => {
    try {
      const status_data = await dispatch(refreshStatusPipeLineAsync())
      return status_data.payload.is_running
    } catch (error) {
      console.error("Error:", error)
    }
  }

  const refresh = async () => {
    if (refreshStatus) {
      return
    }
    let is_running = await refresh_status()
    if (is_running === true) {
      dispatch(
        setAlertData2(
          setAlertData(
            "Data refresh is already running!",
            ALERT_STATUS_MSGS.INFO,
            true,
          ),
        ),
      )
      dispatch(updateRefreshStatus(true))
    } else {
      const data = await dispatch(refreshPipeLineAsync())
      dispatch(updateRefreshStatus(true))
      const message = data.payload.message
      dispatch(
        setAlertData2(setAlertData(message, ALERT_STATUS_MSGS.SUCCESS, true)),
      )
    }
    await new Promise((resolve) => setTimeout(resolve, 10000))
    is_running = await refresh_status()
    let updatedLastRefreshedDate = lastRunDate
    while (updatedLastRefreshedDate === lastRunDate || is_running) {
      await new Promise((resolve) => setTimeout(resolve, 30000))
      const response = await dispatch(
        lastRunAsynclastRunDateToCheckRefreshStatusAsync(),
      )
      updatedLastRefreshedDate = formatDate(response.payload.last_run)
      is_running = await refresh_status()
    }
    dispatch(lastRunAsync())
    dispatch(gantChartAsync())
    dispatch(pipelineCardsAsync())
    dispatch(updateRefreshStatus(false))
    if (
      activeGantSelection?.["extra"]?.["name"] &&
      selectedRunDetails?.["id"]
    ) {
      dispatch(flowDiagramAsync({ reference_pipeline: "" }))
      dispatch(
        deepDiveAsync({
          pipeLineName: activeGantSelection["extra"]["name"],
          id: selectedRunDetails["id"],
        }),
      )
    }

    dispatch(
      setAlertData2(
        setAlertData(
          "Data refreshed successfully!",
          ALERT_STATUS_MSGS.SUCCESS,
          true,
        ),
      ),
    )
  }

  const [currentScreen, setCurrentScreen] = useState("")
  const location = useLocation()
  const [, setIsRawDataQuality] = useState(false)

  useEffect(() => {
    if (location.pathname.includes(SCREENS_DETAILS.DATA_METADATA)) {
      setCurrentScreen(SCREENS_DETAILS.DATA_METADATA)
    } else if (location.pathname.includes(SCREENS_DETAILS.MONITORING)) {
      setCurrentScreen(SCREENS_DETAILS.MONITORING)
    } else if (location.pathname.includes(SCREENS_DETAILS.MODEL_META_DATA)) {
      setCurrentScreen(SCREENS_DETAILS.MODEL_META_DATA)
    } else if (location.pathname.includes(SCREENS_DETAILS.MAINTENANCE)) {
      setCurrentScreen(SCREENS_DETAILS.MAINTENANCE)
    } else if (location.pathname.includes(SCREENS_DETAILS.DATA_QUALITY)) {
      setCurrentScreen(SCREENS_DETAILS.DATA_QUALITY)
    } else if (location.pathname.includes(SCREENS_DETAILS.DATA_HARMONIZATION)) {
      setCurrentScreen(SCREENS_DETAILS.DATA_HARMONIZATION)
    }
  }, [location])

  useEffect(() => {
    if (location.pathname.includes(SCREENS_DETAILS.DATA_METADATA)) {
      setIsRawDataQuality(true)
    }
  }, [location])

  const qualityRefresh = () => {
    dispatch(hitRefresh())
  }

  return (
    <div className={classes.container + " mx-1"}>
      <FilterGroup />
      {currentScreen === SCREENS_DETAILS.MONITORING && (
        <div className={classes.flexContainer2}>
          <div className={classes.columnContainer}>
            <span className={classes.dateLabel}>Last Refreshed:</span>
            <span className={classes.date}>{lastRunDate}</span>
          </div>
          <div className={classes.separator}></div>
          <div className={classes.flexContainer}>
            {refreshStatus ? (
              <img src={RefreshCircle} />
            ) : (
              <CustomIcon path={Refresh} alt="" />
            )}
            <button onClick={refresh} className={classes.refresh}>
              Refresh
            </button>
          </div>
          <button
            className={classes.newPipeLineBtn}
            onClick={handleNewPipeLineRun}
          >
            New Pipeline Run
          </button>
        </div>
      )}
      {currentScreen === SCREENS_DETAILS.DATA_QUALITY && (
        <div className={classes.flexContainer2}>
          {/* <div className={classes.columnContainer}>
            <span className={classes.dateLabel}>Last Refreshed:</span>
            <span className={classes.date}>{lastRunDate}</span>
          </div> */}
          {/* <div className={classes.separator}></div> */}
          <span className="text-md mb-[2px]">|</span>
          <div className={classes.flexContainer}>
            {qualityRefreshStatus === API_STATUS.LOADING ? (
              <img src={RefreshCircle} />
            ) : (
              <CustomIcon path={Refresh} alt="" />
            )}
            <button onClick={qualityRefresh} className={classes.refresh}>
              Refresh
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
